<template>
  <v-container max-width="800" class="py-4">
    <h1 class="text-h6 font-weight-bold mb-4">Mes cartes</h1>

    <v-card class="mb-6">
      <v-card-text class="pa-4">
        <p class="text-body-2 font-weight-medium mb-3">Connecter une carte</p>
        <v-form @submit.prevent="connectCard">
          <v-select
            v-model="formType"
            :items="typeItems"
            item-title="label"
            item-value="value"
            label="Enseigne"
            variant="outlined"
            density="compact"
            class="mb-3"
            hide-details
          />
          <v-text-field
            v-model="formNumber"
            label="Numéro de carte"
            variant="outlined"
            density="compact"
            class="mb-3"
            hide-details
          />
          <v-alert
            v-if="formError"
            type="error"
            variant="tonal"
            density="compact"
            class="mb-3"
          >
            {{ formError }}
          </v-alert>
          <v-btn
            type="submit"
            color="primary"
            variant="elevated"
            :loading="saving"
            :disabled="!formType || !formNumber.trim()"
          >
            Connecter
          </v-btn>
        </v-form>
      </v-card-text>
    </v-card>

    <v-progress-linear v-if="loading" indeterminate color="primary" rounded height="4" class="mb-4" />
    <v-alert v-if="listError" type="error" variant="tonal" class="mb-4">{{ listError }}</v-alert>

    <template v-if="!loading && cards.length === 0">
      <div class="d-flex flex-column align-center justify-center py-8 text-medium-emphasis">
        <v-icon size="56" class="mb-3">mdi-credit-card-outline</v-icon>
        <p class="text-body-1">Aucune carte connectée</p>
        <p class="text-body-2">Ajoutez une enseigne pour le pitch, ou lancez <code>app:demo:fidelity</code>.</p>
      </div>
    </template>

    <div v-else class="d-flex flex-column ga-3">
      <v-card
        v-for="card in cards"
        :key="card.id"
      >
        <div class="d-flex align-center pa-4 ga-3">
          <v-icon color="primary">mdi-credit-card-outline</v-icon>
          <div style="min-width: 0">
            <p class="text-body-1 font-weight-medium mb-0">{{ fidelityCardLabel(card.type) }}</p>
            <p class="text-caption text-medium-emphasis mb-0">{{ card.number }}</p>
          </div>
          <v-spacer />
          <v-chip size="small" variant="tonal">
            {{ card.purchaseCount }} achat{{ card.purchaseCount > 1 ? 's' : '' }}
          </v-chip>
        </div>
      </v-card>
    </div>
  </v-container>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from 'vue'
import { FIDELITY_CARD_TYPES, fidelityCardLabel, type FidelityCardTypeValue } from '../fidelity'

interface FidelityCard {
  id: number
  number: string
  type: FidelityCardTypeValue | string
  purchaseCount: number
}

interface HydraCollection {
  member: FidelityCard[]
}

export default defineComponent({
  name: 'FidelityCardsView',

  setup() {
    const cards      = ref<FidelityCard[]>([])
    const loading    = ref(false)
    const saving     = ref(false)
    const listError  = ref('')
    const formError  = ref('')
    const formType   = ref<FidelityCardTypeValue | null>(null)
    const formNumber = ref('')
    const typeItems  = FIDELITY_CARD_TYPES

    async function fetchCards(): Promise<void> {
      loading.value   = true
      listError.value = ''
      try {
        const res = await fetch('/api/fidelity_cards', {
          credentials: 'include',
          headers: { Accept: 'application/ld+json' },
        })
        if (!res.ok) throw new Error('Impossible de charger les cartes.')
        const data = await res.json() as HydraCollection & { 'hydra:member'?: FidelityCard[] }
        cards.value = data.member ?? data['hydra:member'] ?? []
      } catch (err) {
        listError.value = err instanceof Error ? err.message : 'Une erreur est survenue.'
      } finally {
        loading.value = false
      }
    }

    async function connectCard(): Promise<void> {
      formError.value = ''
      if (!formType.value || !formNumber.value.trim()) return
      saving.value = true

      try {
        const res = await fetch('/api/fidelity_cards', {
          method: 'POST',
          credentials: 'include',
          headers: {
            Accept: 'application/ld+json',
            'Content-Type': 'application/ld+json',
          },
          body: JSON.stringify({
            type: formType.value,
            number: formNumber.value.trim(),
          }),
        })

        if (res.status === 409) {
          throw new Error('Ce numéro est déjà enregistré sur votre compte.')
        }
        if (!res.ok) {
          throw new Error('Impossible de connecter la carte.')
        }

        const created: FidelityCard = await res.json()
        created.purchaseCount = created.purchaseCount ?? 0
        cards.value = [created, ...cards.value]
        formNumber.value = ''
      } catch (err) {
        formError.value = err instanceof Error ? err.message : 'Une erreur est survenue.'
      } finally {
        saving.value = false
      }
    }

    onMounted(fetchCards)

    return {
      cards,
      loading,
      saving,
      listError,
      formError,
      formType,
      formNumber,
      typeItems,
      fidelityCardLabel,
      connectCard,
    }
  },
})
</script>
